import { CommonModule } from '@angular/common';
import { Component, computed, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import type { UserModel } from '../../../../models';
import { canManageMembership, membershipRemovalBlock, sameUsername } from '../../business-unit-membership-policy';

/**
 * Reusable tab component for displaying and managing a list of users within a business unit.
 *
 * Features:
 * - Renders a filterable list of users with avatar initials, name, and email
 * - Provides a case-insensitive filter input that searches by name and email
 * - Optionally shows an "Add User" button controlled via {@link showAddButton}
 * - Protects the last member and administrator while allowing eligible members to leave
 */
@Component({
  selector: 'dtis-admin-business-unit-users-tab',
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatListModule,
    MatTooltipModule,
    MatDividerModule,
    TranslocoModule,
    MatFormFieldModule,
  ],
  template: `  
    <div class="p-6">
      <!-- Title and Add Button -->
      <div class="mb-6 flex items-center justify-between">
        <h2 class="text-title-large font-medium">
          {{ title() | transloco }}
        </h2>
        @if (showAddButton() && canManage()) {
          <button
            matButton="filled"
            color="primary"
            [disabled]="isChanging()"
            (click)="addUser.emit()"
          >
            {{ addButtonLabel() | transloco }}
          </button>
        }
      </div>
      
      <!-- Filter Input -->
      <div class="mb-4 relative flex items-center">
        <input
          class="border-outlineVariant text-onSurfaceVariant focus:border-primary w-full rounded-full border p-2 px-4 focus:outline-none focus:ring-0"
          [class.pr-10]="filterValue() !== ''"
          name="usersFilter"
          [placeholder]="filterPlaceholderKey() | transloco"
          (input)="filterValue.set($any($event.target).value)"
          [value]="filterValue()"
        />
        @if (filterValue() !== '') {
          <button class="absolute! right-2 top-1/2 -translate-y-1/2" mat-icon-button (click)="filterValue.set('')">
            <mat-icon class="text-onSurfaceVariant">close</mat-icon>
          </button>
        }
      </div>

      <!-- User List -->
      <mat-card appearance="outlined">
        <mat-card-content >
          <mat-list >
            @for (user of filteredUsers(); track user.name) {
              <mat-list-item class="h-auto! cursor-pointer transition-colors duration-150 hover:bg-onSurfaceVariant/10 rounded-full!" >
                <div class="flex w-full items-center gap-4 py-3">
                  <!-- Avatar with Initials -->
                  <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-onSurfaceVariant text-surface font-medium">
                    {{ getUserInitials(user.name) }}
                  </div>
                  
                  <!-- User Info -->
                  <div class="flex-1 min-w-0">
                    <div class="text-body-large truncate">{{ user.name }}</div>
                    @if (user.mail) {
                      <div class="text-body-medium text-onSurfaceVariant truncate">{{ user.mail }}</div>
                    }
                  </div>
                  
                  @if (canManage() || sameUsername(user.name, currentUsername())) {
                  <span [matTooltip]="(removalBlock(user) || (sameUsername(user.name, currentUsername()) ? 'membership-leave' : 'remove')) | transloco">
                  <button
                    mat-icon-button
                    [disabled]="!canRemoveUser(user)"
                    [class.opacity-50]="!canRemoveUser(user)"
                    (click)="removeUser.emit(user)"
                    [attr.aria-label]="(sameUsername(user.name, currentUsername()) ? 'membership-leave' : 'remove') | transloco"
                  >
                    <mat-icon class="text-error!">delete</mat-icon>
                  </button>
                  </span>
                  }
                </div>
              </mat-list-item>
            } @empty {
              <div class="text-center text-onSurfaceVariant py-8">
                @if (filterValue()) {
                  {{ 'no-results-found' | transloco }}
                } @else {
                  {{ emptyMessage() | transloco }}
                }
              </div>
            }
          </mat-list>
        </mat-card-content>
      </mat-card>
    </div>
  `,
})
export class BusinessUnitUsersTabComponent {
  /** Full list of users to display. */
  users = input.required<UserModel[]>();
  /** Complete BU membership, not just the role-specific list displayed in this tab. */
  allUsers = input<UserModel[]>();
  isChanging = input(false);
  readonly sameUsername = sameUsername;
  canManage = computed(() => canManageMembership(this.allUsers(), this.currentUsername()));
  /** Username of the currently authenticated user. */
  currentUsername = input.required<string>();
  /** Translation key for the section heading. */
  title = input.required<string>();
  /** Translation key for the add-user button label. */
  addButtonLabel = input.required<string>();
  /** Translation key shown when the (filtered) user list is empty. */
  emptyMessage = input.required<string>();
  /** Whether to show the add-user button. Defaults to `true`. */
  showAddButton = input<boolean>(true);
  /** Translation key for the filter input placeholder. */
  filterPlaceholder = input<string>('filter-users');

  /** Emitted when the user clicks the add-user button. */
  addUser = output<void>();
  /** Emitted with the target {@link UserModel} when a remove button is clicked. */
  removeUser = output<UserModel>();

  /** Computed placeholder key based on input or title fallback. */
  filterPlaceholderKey = computed(() => this.filterPlaceholder());

  /** Current value of the filter input field. */
  filterValue = signal('');
  /** Subset of {@link users} matching the current {@link filterValue} (by name or email). */
  filteredUsers = computed(() => {
    const filter = this.filterValue().toLowerCase();
    if (!filter) {
      return this.users();
    }
    return this.users().filter((u) => u.name.toLowerCase().includes(filter) || (u.mail?.toLowerCase().includes(filter) ?? false));
  });

  /** Returns up to two uppercase initials derived from the given full name. */
  getUserInitials(name: string): string {
    if (!name) {
      return '?';
    }
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  removalBlock(user: UserModel): string | undefined {
    return this.isChanging() ? 'membership-change-in-progress' : membershipRemovalBlock(this.allUsers(), this.currentUsername(), user);
  }

  canRemoveUser(user: UserModel): boolean {
    return !this.removalBlock(user);
  }
}
